import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { DescontoMaximoPerfilComponent } from '../../../../shared/components/desconto-maximo-perfil/desconto-maximo-perfil.component';
import { PerfilOptionStruct } from '../../../../shared/services/structs/usuario/perfil-option.struct';
import {
  PageScrollSpyComponent,
  ScrollSpySection,
} from '../../../../shared/components/page-scroll-spy/page-scroll-spy.component';

import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { MaskService } from '../../../../shared/services/mask.service';
import { ConfigVendasGeralService } from '../../../../shared/services/apis/vendas/config-vendas-geral.service';
import { PerfilService } from '../../../../shared/services/apis/usuario/perfil.service';
import { DescontoPerfilStruct } from '../../../../shared/services/structs/vendas/desconto-perfil.struct';
import { ConfigVendasGeralRequest } from '../../../../shared/services/requests/vendas/config-vendas-geral.request';
import {
  TipoNumeracaoDocumento,
  TipoValidacaoEstoqueNegativo,
} from '../../../../shared/services/models/vendas/config-vendas-geral.model';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { TipoNumeracaoDocumentoEnum } from '../../../../shared/enums/vendas/tipo-numeracao-documento.enum';
import { TipoValidacaoEstoqueNegativoEnum } from '../../../../shared/enums/vendas/tipo-validacao-estoque-negativo.enum';

@Component({
  selector: 'app-config-geral-vendas',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    AlertCardComponent,
    DescontoMaximoPerfilComponent,
    PageScrollSpyComponent,
  ],
  templateUrl: './config-geral-vendas.component.html',
  styleUrls: ['./config-geral-vendas.component.css'],
})
export class ConfigGeralVendasComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;
  public form: FormGroup;
  public isLoading = false;
  public isSaving = false;
  private idConfigVendasGeral: number = 0;
  // Campo que esta tela não edita (é da Configuração do Kanban): guardado no carregamento e
  // devolvido no salvamento para o PUT não reverter o valor da empresa ao default do backend.
  private isMostrarSomaColuna: boolean = true;

  // Menu de navegação das seções, no padrão de produto-cadastro.
  public sections: ScrollSpySection[] = [
    { id: 'numeracao', label: 'Numeração' },
    { id: 'descontos-precos', label: 'Descontos e preços' },
    { id: 'estoques', label: 'Estoques' },
    { id: 'logistica', label: 'Logística' },
    { id: 'condicoes-pagamento', label: 'Condições de pagamento' },
  ];

  public percentMask: any;
  public opcoesPerfis: PerfilOptionStruct[] = [];
  public descontoPerfil: DescontoPerfilStruct[] = [];

  // Rótulo e texto de apoio dos radios de domínio vêm do banco, como em config-orcamento
  // (tipo_impressao_imagem) e config-pdv (tipo_composicao_codigo_barras).
  public opcoesTipoNumeracao: TipoNumeracaoDocumento[] = [];
  public opcoesTipoValidacaoEstoqueNegativo: TipoValidacaoEstoqueNegativo[] = [];

  public readonly descontoItensTooltip =
    'Teto de desconto aplicado a cada item do orçamento e do pedido de venda, para todos os usuários que não ' +
    'tenham teto específico por perfil. Para não limitar, informe 100.';

  public readonly descontoPerfilTooltip =
    'Exceções ao teto geral. Cada perfil listado passa a usar o próprio percentual no lugar do campo Desconto ' +
    'máximo nos itens. Perfis fora da lista continuam com o teto geral. Quando o usuário tem mais de um perfil ' +
    'com teto definido, vale o maior percentual. O limite é validado item a item, no momento em que o desconto ' +
    'é informado.';

  public readonly precedenciaPrecoTooltip =
    'Ativo: entre as listas vigentes que alcançam o produto, o item já entra com o menor preço aplicável.\n\n' +
    'Desativado: o item entra com o preço da lista do cabeçalho; o usuário pode trocar manualmente se quiser ' +
    'aplicar outra lista, como uma promocional.';

  constructor(
    private formBuilder: FormBuilder,
    private configVendasGeralService: ConfigVendasGeralService,
    private perfilService: PerfilService,
    private maskService: MaskService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.percentMask = this.maskService.getPercentMask();
    this.form = this.formBuilder.group({
      isEstornoEstoqueAutomatico: [false, Validators.required],
      isTrabalhaComSeparacao: [true, Validators.required],
      isTrabalhaComExpedicao: [true, Validators.required],
      isCondicaoPagamentoLivre: [true, Validators.required],
      idTipoNumeracaoDocumento: [TipoNumeracaoDocumentoEnum.Sequencial, Validators.required],
      isPriorizarMenorPreco: [true],
      idTipoValidacaoEstoqueNegativo: [
        TipoValidacaoEstoqueNegativoEnum.DepositoSelecionado,
        Validators.required,
      ],
      descontoMaximoItens: [100, [Validators.required, Validators.min(0), Validators.max(100)]],
      isExibirPrecoListaDescontoItens: [true, Validators.required],
    });
  }

  ngOnInit(): void {
    this.carregarDados();
  }

  private carregarDados(): void {
    this.isLoading = true;
    forkJoin({
      config: this.configVendasGeralService.get().pipe(catchError(() => of(null))),
      lookups: this.configVendasGeralService.getLookups().pipe(catchError(() => of(null))),
      perfis: this.perfilService.getAllLookup().pipe(catchError(() => of(null))),
    }).subscribe({
      next: res => {
        this.isLoading = false;

        if (res.lookups && !res.lookups.isError) {
          this.opcoesTipoNumeracao = res.lookups.listTipoNumeracaoDocumento || [];
          this.opcoesTipoValidacaoEstoqueNegativo = res.lookups.listTipoValidacaoEstoqueNegativo || [];
        } else if (res.lookups?.isError) {
          this.alertService.show('Aviso', res.lookups.errorDescription, AlertType.warning);
        }

        if (res.perfis && !res.perfis.isError) {
          this.opcoesPerfis = (res.perfis.listaPerfil || []).map(p => ({
            idPerfil: p.perfil.idPerfil,
            nome: p.perfil.nome,
          }));
        } else if (res.perfis?.isError) {
          this.alertService.show('Aviso', res.perfis.errorDescription, AlertType.warning);
        }

        if (res.config && !res.config.isError && res.config.configVendasGeral) {
          const c = res.config.configVendasGeral;
          this.idConfigVendasGeral = c.idConfigVendasGeral;
          this.isMostrarSomaColuna = c.isMostrarSomaColuna;
          this.form.patchValue({
            isEstornoEstoqueAutomatico: c.isEstornoEstoqueAutomatico,
            isTrabalhaComSeparacao: c.isTrabalhaComSeparacao,
            isTrabalhaComExpedicao: c.isTrabalhaComExpedicao,
            isCondicaoPagamentoLivre: c.isCondicaoPagamentoLivre,
            idTipoNumeracaoDocumento: c.idTipoNumeracaoDocumento,
            isPriorizarMenorPreco: c.isPriorizarMenorPreco,
            idTipoValidacaoEstoqueNegativo: c.idTipoValidacaoEstoqueNegativo,
            descontoMaximoItens: c.descontoMaximoItens,
            isExibirPrecoListaDescontoItens: c.isExibirPrecoListaDescontoItens,
          });
          this.descontoPerfil = res.config.listDescontoPerfil || [];
        } else if (res.config?.isError) {
          this.alertService.show('Aviso', res.config.errorDescription, AlertType.warning);
        }
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  /** Sem rota, volta para o menu de Vendas; com rota, abre a configuração de logística escolhida. */
  public salvar(rotaDestino?: string): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Preencha os campos obrigatórios.', AlertType.error);
      return;
    }

    this.isSaving = true;
    const raw = this.form.getRawValue();
    const request: ConfigVendasGeralRequest = {
      isEstornoEstoqueAutomatico: raw.isEstornoEstoqueAutomatico,
      isTrabalhaComSeparacao: raw.isTrabalhaComSeparacao,
      isTrabalhaComExpedicao: raw.isTrabalhaComExpedicao,
      isCondicaoPagamentoLivre: raw.isCondicaoPagamentoLivre,
      isMostrarSomaColuna: this.isMostrarSomaColuna,
      idTipoNumeracaoDocumento: raw.idTipoNumeracaoDocumento,
      isPriorizarMenorPreco: raw.isPriorizarMenorPreco,
      idTipoValidacaoEstoqueNegativo: raw.idTipoValidacaoEstoqueNegativo,
      descontoMaximoItens: raw.descontoMaximoItens,
      isExibirPrecoListaDescontoItens: raw.isExibirPrecoListaDescontoItens,
      listDescontoPerfil: this.descontoPerfil,
    };
    this.configVendasGeralService.update(this.idConfigVendasGeral, request).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', 'Configurações salvas com sucesso!', AlertType.success);
        if (rotaDestino) this.router.navigate([rotaDestino], { queryParams: { origem: 'gerais' } });
        else this.voltar();
      },
      error: error => {
        this.isSaving = false;
        this.alertService.show('Aviso', error || 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  public get condicaoPagamentoLivreApoio(): string {
    return this.form.get('isCondicaoPagamentoLivre')?.value
      ? 'Quando ativo, o vendedor pode digitar livremente como dividir o pagamento nas formas com parcelamento configurável pela empresa.'
      : 'Quando desativado, o vendedor só pode escolher entre as condições cadastradas, aplicando a taxa de juros de cada uma quando houver.';
  }

  public onDescontoPerfilChange(items: DescontoPerfilStruct[]): void {
    this.descontoPerfil = items;
    this.form.markAsDirty();
  }

  public get precoListaApoio(): string {
    return this.form.get('isExibirPrecoListaDescontoItens')?.value
      ? 'Mostra as colunas Preço de lista e % Desconto na tabela de itens do pedido e do orçamento.'
      : 'As colunas não aparecem em nenhum dos dois documentos — o desconto é aplicado apenas no campo global "Desconto" de cada documento.';
  }


  public get estoqueNegativoTooltip(): string {
    const permite =
      this.form.get('idTipoValidacaoEstoqueNegativo')?.value === TipoValidacaoEstoqueNegativoEnum.Sim;
    return permite
      ? 'O lançamento de estoque é permitido mesmo que o saldo do produto fique negativo. Nenhuma validação de saldo é feita na venda, na nota fiscal ou na ordem de serviço.'
      : 'Não permite lançar estoque de notas fiscais, pedidos de venda e ordens de serviço caso o saldo de algum dos produtos for insuficiente. Para as vendas e notas fiscais, será considerado o depósito selecionado. Para as ordens de serviço, será considerado o depósito padrão.';
  }

  /**
   * Atalho para a configuração da separação ou da expedição. Com alteração pendente, a rotina precisa estar
   * salva antes: confirma e usa o mesmo salvamento da tela, abrindo a configuração só se salvar.
   */
  public abrirConfiguracaoLogistica(rotaDestino: string, nomeRotina: string): void {
    if (!this.form.dirty) {
      this.router.navigate([rotaDestino], { queryParams: { origem: 'gerais' } });
      return;
    }
    this.dialog.open(ConfirmModalComponent, {
      maxWidth: '96vw',
      data: {
        title: 'Salvar as configurações?',
        description: `Para acessar a configuração da ${nomeRotina}, as configurações gerais de vendas precisam ser salvas.`,
        buttonConfirm: 'Salvar e acessar',
        buttonClose: 'Voltar',
      },
    }).afterClosed().subscribe(result => {
      if (result?.confirm) this.salvar(rotaDestino);
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/vendas']);
  }
}
